// src/components/NavBar.jsx
import React, { useState } from 'react';
import {
  AppBar,
  IconButton,
  Toolbar,
  Typography,
  Box,
  Button,
  Menu as MuiMenu,
  MenuList,
  MenuItem,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Divider,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import CabinIcon from '@mui/icons-material/Cabin';
import logo from '../assets/img/logo.png';

// === Tema oscuro sutil (slate-900 style) ===
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#0b1220' },
    background: { default: '#0f172a', paper: '#0b1220' },
    text: { primary: '#ffffff' },
  },
  typography: {
    fontFamily: [
      'Inter', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto',
      'Helvetica Neue', 'Arial', 'Noto Sans', 'sans-serif',
    ].join(','),
    button: { textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 },
  },
});

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({ target: window ? window() : undefined });
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// ---- Estilos ----
const Bar = styled(AppBar)`
  && {
    background: linear-gradient(180deg, rgba(15, 23, 42, .92) 0%, rgba(11, 18, 32, .88) 100%);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(255, 255, 255, .06);
    box-shadow: 0 10px 24px rgba(0,0,0,.28);
    position: sticky;
  }
  &&::after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 2px;
    background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(218,165,32,.9) 50%, rgba(0,0,0,0) 100%);
    box-shadow: 0 0 12px rgba(218,165,32,.35);
    pointer-events: none;
  }
`;

const BrandBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandLogo = styled('img')`
  height: 34px;
  width: auto;
  display: block;
  border-radius: 6px;
  box-shadow: 0 6px 16px rgba(0,0,0,.3);
`;

const BrandName = styled(Typography)`
  && {
    font-weight: 900;
    letter-spacing: .12em;
    font-size: 1.05rem;
    line-height: 1;
    color: #fff;
    text-shadow: 0 1px 0 rgba(0,0,0,.45);
    white-space: nowrap;
  }
`;

// Botón desktop
const NavButton = styled(Button)`
  && {
    font-size: .9rem;
    letter-spacing: .08em;
    padding: 8px 14px;
    margin-right: .25rem;
    border-radius: 10px;
    color: rgba(255,255,255,.92);
    transition: transform .12s ease, box-shadow .25s ease, background .2s ease;
  }
  &&:hover {
    background: rgba(255,255,255,.08);
    box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 10px 18px rgba(0,0,0,.25);
    transform: translateY(-1px);
  }
  &&.active {
    color: #F5DEB3;
    background: linear-gradient(180deg, rgba(218,165,32,.18) 0%, rgba(218,165,32,.06) 100%);
    box-shadow: 0 8px 18px rgba(218,165,32,.22), inset 0 1px 0 rgba(255,255,255,.18);
    border: 1px solid rgba(218,165,32,.35);
  }
`;

// Contenedor “MENU” móvil
const MenuContainer = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 1rem;
  gap: .35rem;
  span {
    font-size: 1rem;
    color: #F5DEB3;
    font-weight: 800;
    letter-spacing: .22em;
  }
`;

const NavBar = (props) => {
  const [anchorNav, setAnchorNav] = useState(null);
  const location = useLocation();

  const openMenu = (event) => setAnchorNav(event.currentTarget);
  const closeMenu = () => setAnchorNav(null);

  const navItems = [
    { to: '/', label: 'Principal' },
    { to: '/about', label: 'Nosotros' },
    { to: '/contact', label: 'Contacto' },
  ];

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <HideOnScroll {...props}>
        <Bar>
          <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 3 } }}>
            {/* Marca */}
            <BrandBox sx={{ mr: 2 }}>
              <BrandLogo src={logo} alt="Ferracort logo" />
              <BrandName className="custom-logo" variant="h6">FERRACORT</BrandName>
            </BrandBox>

            {/* Filler */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Links desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: .5 }}>
              {navItems.map((item) => (
                <NavButton
                  key={item.to}
                  className={`custom-title ${location.pathname === item.to ? 'active' : ''}`}
                  color="inherit"
                  component={Link}
                  to={item.to}
                >
                  {item.label}
                </NavButton>
              ))}
            </Box>

            {/* Menú móvil */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}>
              <IconButton size="large" edge="end" color="inherit" onClick={openMenu}>
                <MenuContainer className="custom-title">
                  <span>ME</span>
                  <CabinIcon sx={{ color: '#F5DEB3' }} />
                  <span>NU</span>
                </MenuContainer>
              </IconButton>

              <MuiMenu
                anchorEl={anchorNav}
                open={Boolean(anchorNav)}
                onClose={closeMenu}
                sx={{
                  '& .MuiPaper-root': {
                    background: '#0b1220',
                    color: 'rgba(255,255,255,.92)',
                    border: '1px solid rgba(255,255,255,.08)',
                    boxShadow: '0 16px 40px rgba(0,0,0,.45)',
                    borderRadius: 2,
                    minWidth: 220,
                  },
                }}
              >
                <MenuList dense disablePadding>
                  {navItems.map((item, idx) => (
                    <Box key={item.to}>
                      <MenuItem
                        className="custom-logo"
                        onClick={closeMenu}
                        component={Link}
                        to={item.to}
                        sx={{
                          py: 1.2,
                          px: 2,
                          fontSize: '.95rem',
                          letterSpacing: '.12em',
                          color:
                            location.pathname === item.to
                              ? '#F5DEB3'
                              : 'rgba(255,255,255,.92)',
                          '&:hover': {
                            background: 'rgba(255,255,255,.08)',
                          },
                        }}
                      >
                        {item.label}
                      </MenuItem>
                      {idx < navItems.length - 1 && (
                        <Divider sx={{ borderColor: 'rgba(255,255,255,.06)' }} />
                      )}
                    </Box>
                  ))}
                </MenuList>
              </MuiMenu>
            </Box>
          </Toolbar>
        </Bar>
      </HideOnScroll>
    </ThemeProvider>
  );
};

export default NavBar;
